import React from 'react';
import { SiteLink, useRouter } from '../../../components';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../services/useAuth';
import env from '../../../env';

function describeReturnUrl(returnUrl) {
  if (!returnUrl) return null;
  if (/^\/checkout\/live-feed/i.test(returnUrl)) return 'to continue with your purchase';
  if (/^\/checkout/i.test(returnUrl) || /^\/cart/i.test(returnUrl)) return 'to complete your purchase';
  if (/^\/watch/i.test(returnUrl)) return 'to watch this stream';
  if (/^\/members-area/i.test(returnUrl)) return 'to access the members area';
  return 'to continue';
}

export function Signup(props) {
  const { returnUrl: returnUrlProp } = props;
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [error, setError] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const router = useRouter();
  const location = useLocation();
  const auth = useAuth();

  const passwordsMatch = password && confirmPassword && password === confirmPassword;

  const fromQuery = new URLSearchParams(location.search || '').get('returnUrl');
  let effectiveReturnUrl = returnUrlProp != null ? returnUrlProp : (location.state?.returnUrl || fromQuery);
  if (!effectiveReturnUrl) {
    try {
      effectiveReturnUrl = sessionStorage.getItem('signupReturnUrl');
    } catch (_) {}
  }
  const contextLabel = describeReturnUrl(effectiveReturnUrl);

  async function submit(e) {
    e.preventDefault();
    e.stopPropagation();

    const trimmedFirst = (firstName || '').trim();
    const trimmedLast = (lastName || '').trim();
    const trimmedEmail = (email || '').trim().toLowerCase();

    if (!trimmedFirst || !trimmedLast) {
      setError('Please enter your first and last name.');
      return;
    }
    if (!trimmedEmail) {
      setError('Please enter your email address.');
      return;
    }
    if (!password || !confirmPassword) {
      setError('Please fill in both password fields.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!agreed) {
      setError('Please agree to the Privacy Policy.');
      return;
    }

    setPending(true);
    setError('');

    try {
      // Public API: server creates Firebase user + profile (no client-side Firebase)
      const payload = {
        email: trimmedEmail,
        password: password,
        firstName: trimmedFirst,
        lastName: trimmedLast
      };

      const url = (env.apiBase || '').replace(/\/$/, '') + '/api/profile/Register';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const responseData = await response.json().catch(() => ({}));
      const apiErrors = responseData.errors && Array.isArray(responseData.errors) ? responseData.errors : [];
      const errorMsg = apiErrors[0] && apiErrors[0].errorMessage ? apiErrors[0].errorMessage : null;

      if (!response.ok || errorMsg) {
        throw new Error(errorMsg || response.statusText || 'Registration failed. Please try again.');
      }

      setPending(false);
      setError('');
      // Prefer returnUrl from prop, then location.state, then query, then sessionStorage (set by Login when linking to sign-up from checkout)
      const fromQuery = new URLSearchParams(location.search || '').get('returnUrl');
      let returnUrl = returnUrlProp != null ? returnUrlProp : (location.state?.returnUrl || fromQuery);
      if (!returnUrl) {
        try {
          returnUrl = sessionStorage.getItem('signupReturnUrl');
          if (returnUrl) sessionStorage.removeItem('signupReturnUrl');
        } catch (_) {}
      }
      const safeUrl = returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')
        ? returnUrl
        : '/cart';
      try {
        await auth.signin(trimmedEmail, password);
      } catch (_) {
        // Account created; if auto-login fails (e.g. backend created user only), still send to payment section
      }
      router.push(safeUrl);
    } catch (err) {
      console.error('Signup error:', err);
      setPending(false);
      setError(err.message || 'Registration failed. Please try again.');
    }
  }

  const showPasswordBtnStyle = {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    color: '#007bff',
    fontSize: '0.85rem',
    background: 'none',
    border: 'none',
    padding: 0
  };

  return (
    <div
      className="sign-up auth-panel auth-panel--centered"
      style={{
        minHeight: '80vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 0'
      }}
    >
      <div className="auth-panel__inner">
        <div className="auth-panel__card" style={{ background: 'white', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <h1 className="h4 mb-1">Register</h1>
          {contextLabel && (
            <p className="text-muted mb-4" style={{ fontSize: '0.95rem' }}>
              Create an account {contextLabel}.
            </p>
          )}
          {!contextLabel && <div className="mb-4" />}
          <form method="POST" noValidate onSubmit={submit}>
            <div className="form-group">
              <label htmlFor="firstName">First name *</label>
              <input
                id="firstName"
                type="text"
                className="form-control"
                name="firstName"
                required
                autoComplete="given-name"
                autoFocus
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last name *</label>
              <input
                id="lastName"
                type="text"
                className="form-control"
                name="lastName"
                required
                autoComplete="family-name"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                type="email"
                className="form-control"
                name="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  name="password"
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} style={showPasswordBtnStyle} onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <small className="form-text text-muted">At least 6 characters</small>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm password *</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-control"
                  name="confirmPassword"
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
                {passwordsMatch && (
                  <span style={{ position: 'absolute', right: '50px', top: '50%', transform: 'translateY(-50%)', color: '#28a745', fontWeight: 'bold', fontSize: '0.85rem' }} aria-hidden>✓</span>
                )}
                <button type="button" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'} style={showPasswordBtnStyle} onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <div className="form-check">
                <input
                  type="checkbox"
                  name="privacy"
                  id="privacy"
                  className="form-check-input"
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  required
                />
                <label htmlFor="privacy" className="form-check-label">
                  I agree to the <SiteLink to="/privacy" target="_blank">Privacy and Confidentiality Policy</SiteLink>
                </label>
              </div>
            </div>
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}
            <div className="form-group m-0">
              <button type="submit" disabled={pending} className="btn btn-primary btn-block">
                {pending && <span className="spinner-grow spinner-grow-sm mr-1" role="status" aria-hidden="true" />}
                {pending ? 'Registering…' : 'Register'}
              </button>
            </div>
            <p className="text-center mt-3 mb-0">
              Already have an account?{' '}
              <SiteLink to={(() => {
                const fromQuery = new URLSearchParams(location.search || '').get('returnUrl');
                const returnUrl = returnUrlProp != null ? returnUrlProp : (location.state?.returnUrl || fromQuery);
                return returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login';
              })()}>
                Sign in
              </SiteLink>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
